import React from 'react';
import Box from '@mui/joy/Box';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';

// AI sparkle icon SVG
const AIIcon = ({ active }: { active: boolean }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ 
            filter: active ? 'drop-shadow(0 0 3px rgba(139, 92, 246, 0.5))' : 'none',
            transition: 'all 0.3s ease'
        }}
    >
        {/* Main sparkle */}
        <path
            d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z"
            fill={active ? "url(#aiGradient)" : "#6B7280"}
            stroke={active ? "url(#aiGradient)" : "#6B7280"}
            strokeWidth="0.5"
            strokeLinejoin="round"
        />
        {/* Small sparkle */}
        <path
            d="M19 8L19.5 10L21.5 10.5L19.5 11L19 13L18.5 11L16.5 10.5L18.5 10L19 8Z"
            fill={active ? "url(#aiGradient)" : "#9CA3AF"}
            opacity={active ? 1 : 0.5}
        />
        <defs>
            <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="50%" stopColor="#EC4899" />
                <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
        </defs>
    </svg>
);

// Rainbow gradient ring animation keyframes (injected via style tag)
const rainbowAnimationStyles = `
@keyframes rainbow-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes pulse-glow {
    0%, 100% { 
        box-shadow: 0 0 8px rgba(139, 92, 246, 0.4),
                    0 0 16px rgba(236, 72, 153, 0.2),
                    0 0 24px rgba(245, 158, 11, 0.1);
    }
    50% { 
        box-shadow: 0 0 12px rgba(139, 92, 246, 0.6),
                    0 0 24px rgba(236, 72, 153, 0.4),
                    0 0 36px rgba(245, 158, 11, 0.2);
    }
}

.ai-sentiment-icon-active {
    animation: pulse-glow 2s ease-in-out infinite;
}

.ai-sentiment-icon-active::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: conic-gradient(
        from 0deg,
        #8B5CF6,
        #EC4899,
        #F59E0B,
        #10B981,
        #3B82F6,
        #8B5CF6
    );
    animation: rainbow-spin 3s linear infinite;
    z-index: -1;
}

.ai-sentiment-icon-active::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 50%;
    background: var(--joy-palette-background-surface, #1a1a2e);
    z-index: -1;
}

.ai-sentiment-icon-inactive {
    opacity: 0.4;
    filter: grayscale(100%);
}
`;

export interface SentimentData {
    id: number;
    summary: string;
    booking_probability?: number | null;
    urgency_level?: string;
    objections?: string;
    communication_count?: number;
    last_communication_type?: string;
    updated_at?: string;
    // Legacy fields kept for backward compat during rollout
    sentiment_type?: string;
    booking_confirmed?: boolean | null;
    booking_confirmation_note?: string;
    customer_satisfaction?: string;
    created_at?: string;
}

interface AISentimentIconProps {
    sentiment: SentimentData | null | undefined;
    showBookingConfirmation?: boolean;
}

export default function AISentimentIcon({ sentiment, showBookingConfirmation = false }: AISentimentIconProps) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
        }
    };

    const isActive = sentiment !== null && sentiment !== undefined;

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderTooltipContent = () => {
        if (!sentiment) {
            return (
                <Box sx={{ p: 1 }} role="status" aria-live="polite">
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        No AI sentiment found
                    </Typography>
                </Box>
            );
        }

        return (
            <Box 
                role="region" 
                aria-label="AI Insights"
                sx={{ 
                    p: 1.5, 
                    maxWidth: 320,
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
                    borderRadius: 'md'
                }}
            >
                {/* Header */}
                <Box 
                    component="header"
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box 
                        aria-hidden="true"
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #F59E0B 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '12px' }}>✨</Typography>
                    </Box>
                    <Typography 
                        component="h3"
                        level="title-sm" 
                        sx={{ 
                            background: 'linear-gradient(90deg, #8B5CF6, #EC4899)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 700
                        }}
                    >
                        AI Insights
                    </Typography>
                    {sentiment.updated_at && (
                        <Typography 
                            component="time"
                            level="body-xs" 
                            sx={{ ml: 'auto', color: 'text.tertiary' }}
                        >
                            {formatDate(sentiment.updated_at)}
                        </Typography>
                    )}
                </Box>

                {/* Summary */}
                <Typography level="body-sm" sx={{ mb: 1.5, lineHeight: 1.6, color: 'text.primary' }}>
                    {sentiment.summary}
                </Typography>

                {/* Booking probability */}
                {sentiment.booking_probability && (
                    <Box 
                        role="group"
                        aria-label="Lead metrics"
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1,
                            p: 1,
                            borderRadius: 'sm',
                            bgcolor: 'background.level1'
                        }}
                    >
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            Booking Probability:
                        </Typography>
                        <Box 
                            aria-label={`${sentiment.booking_probability} out of 10`}
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 'sm',
                                bgcolor: sentiment.booking_probability >= 7 ? 'rgba(34, 197, 94, 0.2)' : 
                                         sentiment.booking_probability >= 4 ? 'rgba(234, 179, 8, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                color: sentiment.booking_probability >= 7 ? '#22c55e' : 
                                       sentiment.booking_probability >= 4 ? '#eab308' : '#ef4444',
                                fontWeight: 700,
                                fontSize: '12px'
                            }}
                        >
                            {sentiment.booking_probability}/10
                        </Box>
                        {sentiment.communication_count != null && sentiment.communication_count > 0 && (
                            <Typography level="body-xs" sx={{ ml: 'auto', color: 'text.tertiary' }}>
                                {sentiment.communication_count} comms
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Objections */}
                {sentiment.objections && (
                    <Box 
                        role="group"
                        aria-label="Customer objections"
                        sx={{ 
                            mb: 1,
                            p: 1,
                            borderRadius: 'sm',
                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)'
                        }}
                    >
                        <Typography level="body-xs" sx={{ color: '#ef4444', fontWeight: 700, mb: 0.5 }}>
                            Objections:
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                            {sentiment.objections}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <style>{rainbowAnimationStyles}</style>
            <Tooltip
                title={renderTooltipContent()}
                placement="right"
                arrow
                variant="outlined"
                sx={{
                    '--Tooltip-arrowSize': '8px',
                    bgcolor: 'background.surface',
                    boxShadow: 'lg',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box
                    component="button"
                    type="button"
                    role="button"
                    tabIndex={0}
                    aria-label={
                        isActive ? `AI Insights: ${sentiment?.summary || 'View customer sentiment'}` :
                        'No AI sentiment available'
                    }
                    aria-haspopup="true"
                    aria-expanded="false"
                    onKeyDown={handleKeyDown}
                    className={isActive ? 'ai-sentiment-icon-active' : 'ai-sentiment-icon-inactive'}
                    sx={{
                        position: 'relative',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        bgcolor: 'background.level1',
                        border: 'none',
                        padding: 0,
                        '&:hover, &:focus': {
                            transform: 'scale(1.1)',
                            outline: 'none',
                        },
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.500',
                            outlineOffset: '2px',
                        }
                    }}
                >
                    <AIIcon active={isActive} />
                </Box>
            </Tooltip>
        </>
    );
}
